import { FormControl, FormGroup, Validators, AbstractControl, AbstractControlOptions } from '@angular/forms';
import * as Utils from '../utils';
import * as Services from '../services';

export const creditCardNumberValidator = (preprocessString: boolean = true) => {
    return (control: FormControl): OLO.Common.IValidators | null => {

        if (!control.value) { return null; }
        /* AMEX contain 15 digits! */
        const isValid: boolean = Utils.CreditCards.validateCardNumber(control.value, preprocessString);
        if (isValid) {
            return null;
        }
        const processedValue: string = Utils.CreditCards.processCardNumber(control.value);

        if (!isValid && processedValue.length === 16) {
            return { 'creditCardNumberFormat': 'invalid credit card number format' };
        }

        return { 'creditCardNumberFormatLength': 'invalid credit card length' };

        // return Helpers.validateCardNumber(control.value, preprocessString) ? null : { 'creditCardNumberFormat': 'invalid credit card number format' };
    };
};

export const expiryDateInSingleFieldValidator = () => {
    return (control: FormControl): OLO.Common.IValidators | null => {

        if (!control.value) {
            return null;
        }

        let match = control.value.match(/^\s*(0?[1-9]|1[0-2])\/(\d\d|\d{4})\s*$/);
        if (!match) {
            return { 'expiryDateFormat': 'invalid expiry date format' };
        }

        let yearAsNumber = +(control.value.split('/')[1]);
        let monthAsNumber = +(control.value.split('/')[0]);

        const now = new Date();
        const hours: number[] = [23, 59, 59, 999];
        const toValidateMaxDays: number = Utils.Dates.getDaysInMonth(Number(now.getFullYear()), Number(now.getMonth() + 1));

        const currEdgeDateStamp: number = new Date(now.getFullYear(), now.getMonth() + 1, now.getDay(), ...hours).getTime();

        const toValidateEdgeDateStamp: number = new Date(Number(yearAsNumber) + 2000, Number(monthAsNumber), toValidateMaxDays, ...hours).getTime();

        if (toValidateEdgeDateStamp < currEdgeDateStamp) {
            return { 'expiryDateExpired': 'your card has expired' };
        }

        return null;

    };
};


export const creditCardYearValidator = (control: FormControl): OLO.Common.IValidators | null => {
    if (!control.value) { return null; }

    const currDate: number = new Date().getFullYear() - 2000;

    switch (true) {
        case isNaN(control.value):
            return {
                notANumber: 'invalid year',
            };

        case Number(control.value) < Number(currDate):
            return {
                pastYear: 'year passed',
            };

        default:
            return null;
    }
};

export const creditCardMonthValidator = (control: FormControl): OLO.Common.IValidators | null => {
    if (!control.value) return null;

    const no: number = Number(control.value);

    switch (true) {
        case control.value.length > 2:
        case isNaN(control.value):
        case no < 1 || no > 12:
            return {
                notAMonth: 'invalid month',
            };

        default:
            return null;
    }
};

export const creditCardExpDateValidator = (monthField: string, yearField: string, timeout: number = 500) => {
    /* Validate exp date overall if it's not past */
    return (control: AbstractControl): Promise<OLO.Common.IValidators | null> => {

        return new Promise(resolve => {
            setTimeout(() => {
                const month: AbstractControl = (control as FormGroup).parent.controls[monthField];
                const year: AbstractControl = (control as FormGroup).parent.controls[yearField];

                let currentControl: string = monthField;
                let nextControl: string = yearField;
                if (control === year) {
                    currentControl = yearField;
                    nextControl = monthField;
                }
                const nextCtrl: AbstractControl = ((control as FormGroup).parent.controls[nextControl] as AbstractControl);

                const date: Date = new Date();
                const currMonth: number = date.getMonth() + 1;
                const currYear: number = date.getFullYear();
                const currMaxDays: number = Utils.Dates.getDaysInMonth(currYear, currMonth);
                const userMaxDays: number = Utils.Dates.getDaysInMonth(month.value, year.value);

                const currEdgeDate = new Date(`${currYear}-${currMonth < 10 ? `0${currMonth}` : `${currMonth}`}-${currMaxDays}T23:59:59.999Z`);
                const userDate = new Date(`20${year.value}-${month.value}-${userMaxDays}T23:59:59.999Z`);

                if (userDate < currEdgeDate) {
                    return resolve({
                        'expiredCard': `Your card has expired`
                    });
                }
                if (nextCtrl.status === 'INVALID') {
                    nextCtrl.updateValueAndValidity({ onlySelf: true });
                }
                return resolve(null);
            }, timeout);
        });
    };
};

export const creditCardCVVCIDValidator = (ctrlPath: string, timeout: number = 500) => {
    return (control: AbstractControl): Promise<OLO.Common.IValidators | null> => new Promise(resolve => {
        /* Use reccurency to find form root controller */
        const form: FormGroup = (function findMainParent(ctrl: AbstractControl | FormGroup | FormControl) {
            if (!ctrl.parent) {
                return ctrl as FormGroup;
            }
            return findMainParent(ctrl.parent);
        })(control);

        if (!form || form && typeof form.get === 'undefined') {
            resolve(null);
        }

        const cardControl: AbstractControl = form.get(ctrlPath);
        if (!cardControl) {
            resolve(null);
        }

        setTimeout(() => {
            if (!control || !control.value || !cardControl) {
                resolve(null);
                return;
            }
            const cLength = control.value.length;

            const cardType: OLO.Enums.CREDIT_CARD_TYPES = Utils.CreditCards.detectCardType(cardControl.value);

            if (!Number.isInteger(+control.value)) {
                resolve({ 'cvvValidationError': 'number is invalid' });
            }

            if (cardType === OLO.Enums.CREDIT_CARD_TYPES.AMERICAN_EXPRESS) {
                /* for amex CID */

                if (cLength > 4) {
                    resolve({ 'cvvValidationError': 'CID number too long' });
                } else if (cLength < 3) {
                    resolve({ 'cvvValidationError': 'CID number too short' });
                }

                resolve(null);
            }

            /* for regular check CVV */
            if (cLength > 3) {
                resolve({ 'cvvValidationError': 'CVV number too long' });
            } else if (cLength < 3) {
                resolve({ 'cvvValidationError': 'CVV number too short' });
            }

            resolve(null);

        }, timeout);
    });
};
